import { Button } from 'src/components/button'
import { NoSymbolIcon, PlusIcon } from '@heroicons/react/24/outline'
import { useQueries, useQuery } from '@tanstack/react-query'
import { reservationQueries } from '../services/reservations/queries'
import { useNavigate, useSearchParams } from 'react-router'
import { ReservationCard } from '../components/reservation-card'
import { useEffect, useMemo, useState } from 'react'
import { PageHeader } from 'src/components/layout/page-header'
import { MainContent } from 'src/components/layout/main-content'
import { ContentPane } from 'src/components/layout/content-pane'
import { Input } from 'src/components/input'
import { usePagination } from 'src/hooks/use-pagination'
import { useInfinitePagination } from 'src/hooks/use-infinite-pagination'
import { Pagination } from '@thinknimble/tn-models'
import { PaginationComponent } from 'src/components/pagination'
import { ItemCollection } from 'src/components/layout/item-collection'
import { ListToolbar } from 'src/components/layout/list-toolbar'
import { SegmentedControl } from 'src/components/segmented-control'
import { useViewMode } from 'src/hooks/use-view-mode'
import { usePermissions } from 'src/hooks/use-permissions'
import { useTranslation } from 'react-i18next'
import { Spinner } from 'src/components/spinner'
import { OrganizationStrip } from 'src/components/reservations/organization-strip'
import { PropertyStrip } from 'src/components/reservations/property-strip'
import { organizationQueries } from 'src/services/user/queries'
import { propertyQueries } from 'src/services/properties/queries'
import { propertyApi } from 'src/services/properties/api'
import { reservationApi } from 'src/services/reservations/api'
import type { OrganizationShape } from 'src/services/user/models'
import type { Property } from 'src/services/properties/models'

type CheckInView = 'current' | 'past' | 'future'

const ORG_PAGE_SIZE = 6
const PROPERTY_PAGE_SIZE = 12

export function Reservations() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [checkInView, setCheckInView] = useState<CheckInView>('current')
  const [searchQuery, setSearchQuery] = useState<string>('')
  const { hasPermission } = usePermissions()
  const [viewMode, setViewMode] = useViewMode('reservations', 'card')
  const navigate = useNavigate()

  const orgParam = searchParams.get('org')
  const propertyParam = searchParams.get('property')

  // ----- Organizations strip (infinite scroll + page-level pagination) -----
  const orgsInfinite = useInfinitePagination<OrganizationShape>({
    pageSize: ORG_PAGE_SIZE,
  })
  const { data: orgsResponse, isFetching: orgsFetching } = useQuery(
    organizationQueries.list({
      filters: { hasProperties: 'true' },
      pagination: orgsInfinite.pagination,
      paginationCallback: orgsInfinite.paginationCallback,
    }),
  )
  const { applyData: applyOrgsData, setIsFetching: setOrgsFetching } = orgsInfinite
  useEffect(() => {
    applyOrgsData(orgsResponse)
  }, [orgsResponse, applyOrgsData])
  useEffect(() => {
    setOrgsFetching(orgsFetching)
  }, [orgsFetching, setOrgsFetching])

  const organizations = orgsInfinite.items
  const orgsTotalCount = orgsResponse?.count ?? 0
  const orgsTotalPages = Math.max(
    1,
    orgsInfinite.pagination.calcTotalPages(orgsInfinite.pagination),
  )

  // ----- Properties strip (infinite scroll, scoped to selected org) -----
  const propertiesInfinite = useInfinitePagination<Property>({
    pageSize: PROPERTY_PAGE_SIZE,
    resetKey: orgParam,
  })
  const { data: propertiesResponse, isFetching: propertiesFetching } = useQuery({
    ...propertyQueries.list({
      filters: orgParam ? { organization: orgParam } : undefined,
      pagination: propertiesInfinite.pagination,
      paginationCallback: propertiesInfinite.paginationCallback,
    }),
    enabled: Boolean(orgParam),
  })
  const { applyData: applyPropertiesData, setIsFetching: setPropertiesFetching } =
    propertiesInfinite
  useEffect(() => {
    applyPropertiesData(propertiesResponse)
  }, [propertiesResponse, applyPropertiesData])
  useEffect(() => {
    setPropertiesFetching(propertiesFetching)
  }, [propertiesFetching, setPropertiesFetching])

  const propertiesForSelectedOrg = propertiesInfinite.items

  // Auto-pick the first org when URL has none and orgs are loaded
  useEffect(() => {
    if (!orgParam && organizations.length > 0) {
      const next = new URLSearchParams(searchParams)
      next.set('org', organizations[0].id)
      setSearchParams(next, { replace: true })
    }
  }, [orgParam, organizations, searchParams, setSearchParams])

  // Auto-pick the first property when the current one isn't valid for the selected org
  useEffect(() => {
    if (!orgParam || propertiesFetching) return
    const currentIsValid =
      propertyParam && propertiesForSelectedOrg.some((r) => r.id === propertyParam)
    if (!currentIsValid && propertiesForSelectedOrg.length > 0) {
      const next = new URLSearchParams(searchParams)
      next.set('property', propertiesForSelectedOrg[0].id)
      setSearchParams(next, { replace: true })
    } else if (propertiesForSelectedOrg.length === 0 && propertyParam && !propertiesFetching) {
      const next = new URLSearchParams(searchParams)
      next.delete('property')
      setSearchParams(next, { replace: true })
    }
  }, [
    orgParam,
    propertyParam,
    propertiesForSelectedOrg,
    propertiesFetching,
    searchParams,
    setSearchParams,
  ])

  // Property count per visible org (one tiny query per org). The strip only
  // shows the orgs we've already loaded so this scales linearly with what's
  // on screen.
  const propertyCountQueries = useQueries({
    queries: organizations.map((org) => ({
      queryKey: ['properties', 'count', org.id],
      queryFn: async () => {
        const res = await propertyApi.list({
          filters: { organization: org.id },
          pagination: new Pagination({ size: 1 }),
        })
        return { orgId: org.id, count: res.count }
      },
      enabled: Boolean(org.id),
    })),
  })
  const propertyCountByOrg = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const q of propertyCountQueries) {
      if (q.data) counts[q.data.orgId] = q.data.count
    }
    return counts
  }, [propertyCountQueries])

  // Reservation counts per property for the current check-in view (fires in parallel)
  const reservationCountQueries = useQueries({
    queries: propertiesForSelectedOrg.map((property) => ({
      queryKey: ['reservations', 'count', property.id, checkInView],
      queryFn: async () => {
        const res = await reservationApi.list({
          // Blocks hold dates but aren't stays, so they stay out of the counts.
          filters: { property: property.id, checkInView, kind: 'reservation' },
          pagination: new Pagination({ size: 1 }),
        })
        return { propertyId: property.id, count: res.count }
      },
      enabled: Boolean(property.id),
    })),
  })
  const reservationCountByProperty = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const q of reservationCountQueries) {
      if (q.data) counts[q.data.propertyId] = q.data.count
    }
    return counts
  }, [reservationCountQueries])

  // ----- Reservations grid pagination (unchanged) -----
  const {
    pagination: reservationPagination,
    paginationCallback,
    goToNextPage,
    goToPrevPage,
    goToPage,
  } = usePagination()

  const { data, isLoading } = useQuery({
    ...reservationQueries.list({
      filters: {
        checkInView,
        search: searchQuery,
        ...(propertyParam ? { property: propertyParam } : {}),
      },
      pagination: reservationPagination,
      paginationCallback,
    }),
    enabled: Boolean(propertyParam),
  })

  const handleOrgSelect = (orgId: string) => {
    const next = new URLSearchParams(searchParams)
    next.set('org', orgId)
    next.delete('property')
    setSearchParams(next)
  }

  const handlePropertySelect = (propertyId: string) => {
    const next = new URLSearchParams(searchParams)
    next.set('property', propertyId)
    setSearchParams(next)
  }

  const showOrgStrip = orgsTotalCount > 1
  const hasNoOrgs = !orgsFetching && organizations.length === 0 && orgsTotalCount === 0
  const showOrgPagination = orgsTotalPages > 1

  const canCreate = hasPermission('reservations.create')

  return (
    <>
      <PageHeader
        title={t('reservations.title')}
        actions={
          canCreate && (
            <>
              <Button
                variant="secondary"
                icon={<NoSymbolIcon />}
                onClick={() =>
                  navigate(
                    `/reservations/reservation?kind=block${
                      propertyParam ? `&property=${propertyParam}` : ''
                    }`,
                  )
                }
                data-testid="block-dates-button"
              >
                {t('reservations.blockDates')}
              </Button>
              <Button
                variant="primary"
                icon={<PlusIcon />}
                onClick={() => navigate('/reservations/reservation')}
              >
                {t('reservations.addNew')}
              </Button>
            </>
          )
        }
      >
        <Input
          type="text"
          placeholder={t('reservations.searchPlaceholder')}
          onChange={(e) => {
            setSearchQuery(e.target.value)
          }}
        />
      </PageHeader>
      <MainContent>
        <ContentPane>
          {hasNoOrgs ? (
            <div className="flex flex-col items-center justify-center py-16">
              <h2 className="text-center text-xl text-gray-700">
                {t('reservations.noOrganizations')}
              </h2>
            </div>
          ) : (
            <div className="flex flex-col gap-6">
              {showOrgStrip ? (
                <section>
                  <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-text-placeholder">
                    {t('reservations.organizationsLabel')}
                  </h2>
                  <OrganizationStrip
                    organizations={organizations}
                    propertyCountByOrg={propertyCountByOrg}
                    selectedOrgId={orgParam}
                    onSelect={handleOrgSelect}
                    isLoading={orgsFetching}
                    sentinelRef={orgsInfinite.sentinelRef}
                    isFetchingNext={orgsInfinite.isLoadingNextPage}
                    hasMore={orgsInfinite.pagination.hasNextPage}
                  />
                  {showOrgPagination ? (
                    <PaginationComponent
                      pagination={orgsInfinite.pagination}
                      isLoading={orgsFetching}
                      onNextPage={orgsInfinite.goToNextPage}
                      onPrevPage={orgsInfinite.goToPrevPage}
                      onGoToPage={orgsInfinite.goToPage}
                      showTotalCount={false}
                      className="mt-2"
                    />
                  ) : null}
                </section>
              ) : null}

              <section>
                <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-text-placeholder">
                  {t('reservations.propertiesLabel')}
                </h2>
                <PropertyStrip
                  properties={propertiesForSelectedOrg}
                  reservationCountByProperty={reservationCountByProperty}
                  selectedPropertyId={propertyParam}
                  onSelect={handlePropertySelect}
                  isLoading={propertiesFetching}
                  sentinelRef={propertiesInfinite.sentinelRef}
                  isFetchingNext={propertiesInfinite.isLoadingNextPage}
                  hasMore={propertiesInfinite.pagination.hasNextPage}
                />
              </section>

              <section className="flex flex-col gap-4">
                <ListToolbar
                  filters={
                    <SegmentedControl<CheckInView>
                      aria-label={t('reservations.filters')}
                      value={checkInView}
                      onChange={setCheckInView}
                      data-testid="check-in-view"
                      options={[
                        { value: 'current', label: t('reservations.currentRes') },
                        { value: 'past', label: t('reservations.pastRes') },
                        { value: 'future', label: t('reservations.futureRes') },
                      ]}
                    />
                  }
                  count={data?.count}
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                />
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Spinner size="md" />
                  </div>
                ) : data?.results?.length ? (
                  <>
                    <ItemCollection viewMode={viewMode}>
                      {data.results.map((res: any) => (
                        <ReservationCard key={res.id} reservation={res} viewMode={viewMode} />
                      ))}
                    </ItemCollection>
                    <PaginationComponent
                      pagination={reservationPagination}
                      isLoading={isLoading}
                      onNextPage={goToNextPage}
                      onPrevPage={goToPrevPage}
                      onGoToPage={goToPage}
                      className="mt-4"
                    />
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8">
                    <h1 className="text-blue text-2xl">{t('reservations.noFound')}</h1>
                  </div>
                )}
              </section>
            </div>
          )}
        </ContentPane>
      </MainContent>
    </>
  )
}
